/**
 * The camera system. (§7)
 *
 * "The camera is a gameplay system, not a visual one. In 11v11, the camera IS
 * the game." That is why this file has no Three.js in it: it computes a pose
 * (position, look-at, FOV) from world state, and renderer.ts copies that onto a
 * PerspectiveCamera. Keeping it pure means the off-ball blend and the dead zone
 * are testable, and they are the two things that decide whether 11v11 is
 * playable (§7.3).
 *
 * ── smoothDamp, never lerp (§7.2) ──────────────────────────────────────────
 *
 * `pos += (target - pos) * k` never converges — it asymptotes, so the camera is
 * permanently a little behind the play and feels mushy. It is also frame-rate
 * dependent, so the game feels different at 144Hz. smoothDamp() is a
 * critically-damped spring: it reaches the target, it does not overshoot, and it
 * is parameterized in SECONDS, which is a number a designer can reason about.
 *
 * ── Dizziness fix (live playtest defect, 2026-07-18) ────────────────────────
 *
 * User report: "Close-player camera currently shakes and causes dizziness."
 * Measured before touching anything (synthetic ±0.3m/10Hz player jitter while
 * sprinting + a bouncing ball, see camera.test.ts's "dizziness" suite): the
 * close-player cams (PlayerCam/ProCam) had a look-at target built from the RAW,
 * unsmoothed player position every frame — the camera POSITION was springy, but
 * where it AIMED was not damped at all. Measured look-direction angular
 * acceleration ~45-63 rad/s² (PRE-FIX). Root cause, not a tuning number.
 *
 * The fix is a genuine two-stage cascade, not just a shorter smoothTime:
 *   1. A "target" stage smooths the raw follow input (player position, yaw,
 *      ball position) BEFORE anything derives a camera position or a look-at
 *      point from it — so the look-at is built from the SAME smoothed number
 *      the camera position is, never a raw one.
 *   2. A "camera" stage (the pre-existing spring) then smooths the camera's
 *      own position toward that already-smoothed target.
 * Two independent critically-damped springs roll off high-frequency noise at
 * -24dB/octave instead of one spring's -12dB/octave — the difference between
 * "a runner's per-step sway, barely visible" and "shakes and causes dizziness".
 * Rotation and zoom additionally get an EXPLICIT acceleration ceiling
 * (ROTATION_ACCEL_CAP_RAD_S2 / ZOOM_ACCEL_CAP_DEG_S2) — a designer-facing
 * contract independent of smoothTime tuning, so a sprint-stop 180° reversal or
 * a shot's FOV kick eases in over a bounded time rather than snapping in one
 * frame no matter how the springs are re-tuned later.
 *
 * ── Stand/touchline occlusion (live playtest defect, 2026-07-18) ────────────
 *
 * User report: near the touchlines "the camera must not enter or become
 * blocked by the audience." Measured cause: the broadcast rig pulls the camera
 * `distance` (25m, TELE 32.5m) further out along +Z than its own follow anchor
 * — an anchor already near the far touchline (bounded only by a loose framing
 * margin) plus that pull-back put the camera PAST the stand's solid front wall,
 * worse still at corners where the bowl's octagonal chamfer cuts closer than an
 * axis-aligned margin would suggest. Fixed with a PURE geometric clamp (no
 * three.js, no raycasts — §7.2's purity constraint): as the follow target nears
 * the stand's real footprint (mirrored from render/stadium.ts's SIDE_MARGIN /
 * END_MARGIN / TIER1.depth0 — see the STAND_* constants below), the camera
 * smoothly blends its pull-back distance down and its height up (closer AND
 * higher, so the player stays framed), backstopped by an exact convex-polygon
 * projection that GUARANTEES the final pose never crosses the boundary.
 *
 * ── Live tunables ──────────────────────────────────────────────────────────
 *
 * This file reads TUNABLES.camera every frame rather than importing the
 * constants directly, so the dev overlay (§15.5) actually retunes the camera
 * live. That is deliberate and it is the exception: see devtools/overlay.ts for
 * why the ball/player/shooting groups can NOT work the same way today.
 */

import {
  CAMERA_ANTICIPATION_S,
  CAMERA_BALL_WEIGHT,
  CAMERA_BROADCAST_DISTANCE,
  CAMERA_BROADCAST_FOV,
  CAMERA_BROADCAST_HEIGHT,
  CAMERA_BROADCAST_TILT_DEG,
  CAMERA_DEAD_ZONE,
  CAMERA_OFFBALL_BLEND_FULL,
  CAMERA_OFFBALL_BLEND_START,
  CAMERA_PLAYER_WEIGHT,
  CAMERA_SMOOTH_TIME,
  CAMERA_SPRINT_FOV_KICK,
  TUNABLES,
} from '@fg/shared/sim';
import { DEG2RAD, clamp, clamp01, smoothDamp, type SmoothDampState } from '@fg/shared/math';

export const enum CameraMode {
  /**
   * Classic TV angle — the EA-FC broadcast look of DECISIONS.md D0, and a FIXED
   * touchline rig (no yaw term anywhere in updateFieldCam, by design).
   *
   * It was the default until 2026-07-25. It is not any more, and nothing about
   * the rig itself changed: a fixed azimuth means the screen-centre crosshair —
   * which is now the strike aim — could only ever point at the far touchline
   * here, so the DEFAULT had to become a camera whose azimuth the player owns.
   * See ui/settings.ts DEFAULT_CAMERA_MODE for the alternatives weighed, and
   * game/crosshairAim.ts's `cameraAimsWithCrosshair` for the guard that keeps
   * this mode's reticle honest. Still one C-press (or one dropdown) away.
   */
  Broadcast = 0,
  /** Higher/wider. Best for 11v11 — you can see shape. */
  Tele = 1,
  /** Over-the-shoulder, tight. Immersive, best for 2v2/3v3. */
  PlayerCam = 2,
  /** Broadcast + speed-based FOV widening + subtle shake. */
  Dynamic = 3,
  /** Spectator/replay only — driven externally, ignores the ball. */
  Free = 4,
  /** 3rd-person locked to your player — the "Pro Clubs" cam for 11v11. */
  ProCam = 5,
  /**
   * §10 "Immersive Tactical" — an elevated cam that FOLLOWS the play (the same
   * weighted centroid the broadcast cam tracks) but ORBITS around it on the
   * mouse-look yaw ALONE. Movement, the pass and the ball never rotate it (that
   * is the §10 "no yaw coupling" contract); only the mouse does, and when the
   * mouse moves it wins outright (there is no auto-recentre fighting it). Sits
   * between Broadcast (fixed, can't see behind you) and the shoulder cams (glued
   * to one player) — high enough to read shape, free enough to look where you want.
   */
  TacticalFollow = 6,
}

/**
 * §10 recovery presets. The camera-cycle key rotates exactly these three named
 * views — the set the brief asks for — while the finer-grained modes above stay
 * reachable through the settings screen for power users. `mode` is the engine
 * mode each preset drives; the name is what the HUD/debug panel shows.
 */
export interface CameraPreset {
  readonly id: string;
  readonly name: string;
  readonly mode: CameraMode;
}
export const CAMERA_PRESETS: readonly CameraPreset[] = [
  { id: 'broadcast', name: 'Broadcast', mode: CameraMode.Broadcast },
  { id: 'tactical', name: 'Immersive Tactical', mode: CameraMode.TacticalFollow },
  { id: 'chase', name: 'Close Chase', mode: CameraMode.ProCam },
];

/** The preset a mode belongs to (for the HUD/debug panel), or null if the mode
 *  is only reachable via settings (Tele / PlayerCam / Dynamic / Free). */
export function cameraPreset(m: CameraMode): CameraPreset | null {
  return CAMERA_PRESETS.find((p) => p.mode === m) ?? null;
}

/** Cycle order for the camera-cycle key: the three §10 presets. Free is omitted
 *  — it is not a play cam. */
export const CAMERA_CYCLE: readonly CameraMode[] = CAMERA_PRESETS.map((p) => p.mode);

export function cameraModeName(m: CameraMode): string {
  switch (m) {
    case CameraMode.Broadcast: return 'BROADCAST';
    case CameraMode.Tele: return 'TELE';
    case CameraMode.PlayerCam: return 'PLAYER';
    case CameraMode.Dynamic: return 'DYNAMIC';
    case CameraMode.Free: return 'FREE';
    case CameraMode.ProCam: return 'PRO';
    case CameraMode.TacticalFollow: return 'TACTICAL';
  }
}

/** Everything the camera needs from the world. Plain numbers — no coupling. */
export interface CameraInput {
  ballX: number; ballY: number; ballZ: number;
  ballVX: number; ballVZ: number;
  /** The CONTROLLED player. During a §4.8 switch this is the blended position. */
  playerX: number; playerY: number; playerZ: number;
  playerYaw: number;
  /** Ground speed, for the Dynamic FOV widening. */
  playerSpeed: number;
  sprinting: boolean;
  pitchHalfLength: number;
  pitchHalfWidth: number;
  /** Seconds since match start — drives Dynamic's shake oscillator phase. */
  elapsedS: number;
  /** §10 Ball Focus (opt-in): 0 = off, 1 = soft, 2 = full. Biases the look-at
   *  toward the CURRENT ball (never a predicted destination) so it stays framed;
   *  the mouse still owns the orbit, so manual look always wins. Default 0. */
  ballFocus?: number;
}

/** Look-at bias toward the current ball per Ball Focus level (off/soft/full). */
const BALL_FOCUS_WEIGHT = [0, 0.3, 0.6] as const;

export interface CameraPose {
  x: number; y: number; z: number;
  lookX: number; lookY: number; lookZ: number;
  fov: number;
  /** §7.3: beyond CAMERA_OFFBALL_BLEND_FULL you get an on-screen ball arrow. */
  showBallArrow: boolean;
  /** World-space yaw from the player toward the ball, for that arrow. */
  ballArrowYaw: number;
  /** 0..1 off-ball blend, surfaced for the HUD/dev overlay. */
  offBallBlend: number;
}

// ── Per-mode shaping. These are presentation, not gameplay tunables (§15.5
// covers the sim); they live here because nothing else can use them. ─────────

/** TELE is broadcast, pulled up and back so you can read team shape. */
const TELE_HEIGHT_SCALE = 1.4;
const TELE_DISTANCE_SCALE = 1.3;
const TELE_FOV_BONUS = 6;
/** PLAYER_CAM: over-the-shoulder. Tight enough to be immersive, not nauseating. */
const PLAYER_CAM_BACK = 4.5;
const PLAYER_CAM_HEIGHT = 2.4;
const PLAYER_CAM_FOV = 62;
const PLAYER_CAM_LOOK_AHEAD = 9;
/** PRO_CAM: the Pro Clubs 3rd-person cam — further out, you must see your space. */
const PRO_CAM_BACK = 9;
const PRO_CAM_HEIGHT = 5;
const PRO_CAM_FOV = 60;
const PRO_CAM_LOOK_AHEAD = 6;
/** TACTICAL (§10 "Immersive Tactical"): the broadcast follow target, but elevated
 *  and orbited on the mouse-look yaw. Higher/further than broadcast so shape reads,
 *  and it looks DOWN at the play from a bounded pitch so the space ahead is visible. */
const TACTICAL_HEIGHT_SCALE = 1.25;
const TACTICAL_DISTANCE_SCALE = 1.15;
const TACTICAL_FOV_BONUS = 8;
/** The look-at rides this far up off the ground toward chest height, so the cam
 *  frames players rather than aiming at their feet. */
const TACTICAL_LOOK_HEIGHT = 1.2;
/** DYNAMIC widens with speed and adds a shake you should barely notice — and
 *  only on a genuinely hard hit (see SHAKE_* below); never during normal play. */
const DYNAMIC_FOV_PER_SPEED = 0.7; // degrees per m/s
const DYNAMIC_SHAKE_AMPLITUDE = 0.05; // metres, at full envelope strength
const DYNAMIC_SHAKE_HZ = 6.3;
/**
 * §7.3 — the camera widens as you drift off the ball so your space stays visible.
 *
 * Kept modest deliberately. The original 12° was sized to fit BOTH you and the
 * ball in frame while the target sat at your midpoint; now that the off-ball
 * blend converges on the player (see updateFieldCam step 3) and the ball is
 * represented by the direction arrow, a big widen only shrinks the player you
 * are trying to watch — measured 72° live, which reads as "the camera is not
 * following me" even while the player is technically on screen. This still
 * opens up enough to show the space around you, which is §7.3's actual point.
 */
const OFFBALL_FOV_WIDEN = 5;
/** Keep the camera inside the stadium (§7.2 "Bounds"). Metres beyond the pitch.
 *  This is a loose FRAMING limit on the follow anchor, not a solid-geometry
 *  guarantee — the STAND_* constants below own that guarantee. */
const BOUNDS_MARGIN = 8;
/** Height/FOV changes ease faster than position — a laggy FOV reads as a zoom wobble. */
const FOV_SMOOTH_TIME = 0.18;

// ── Dizziness fix: two-stage cascade + explicit acceleration caps (§7.2) ────
/**
 * Stage 1 ("target"): smooths the RAW follow input (player position/yaw, ball
 * position) before anything derives a camera position or look-at from it.
 * Stage 2 ("camera", the pre-existing spring below) then smooths the camera
 * itself toward that already-smoothed target. Two independent critically-
 * damped springs give -24dB/octave instead of one spring's -12dB/octave —
 * measured to cut the close-cam dizziness metric by >99% (see camera.test.ts).
 * Kept short relative to the camera-stage smoothTime so the ADDED lag stays
 * small — this is a noise filter, not a lag budget.
 */
const TARGET_SMOOTH_TIME = 0.1;
/** The ball gets its own short low-pass before it enters the weighted centroid
 *  / horizon-tilt calc — item §7.2 "low-pass the ball-follow contribution". A
 *  bouncing ball is the single noisiest input the camera reads every frame;
 *  ANTICIPATION (below) still uses the RAW velocity so a genuine hard pass is
 *  led immediately — only the ball's raw jittery POSITION is filtered. */
const BALL_FOLLOW_SMOOTH_TIME = 0.12;
/** Hard ceiling on the camera's yaw acceleration (shoulder cams) — independent
 *  of smoothTime tuning, this is the actual "never snap" contract: a full 180°
 *  reversal eases in over several frames rather than arriving in one. */
const ROTATION_ACCEL_CAP_RAD_S2 = 18;
/** Same idea for FOV/zoom, in the unit a designer actually tunes (deg/s²): a
 *  hard shot's FOV kick or the off-ball widen must ease in, never jump. */
const ZOOM_ACCEL_CAP_DEG_S2 = 220;

// ── Camera shake: an impulse for hard hits, OFF during normal play (§7.2) ───
/**
 * User report: the camera shook during ordinary running. DYNAMIC's shake used
 * to run unconditionally, all the time, at a constant low amplitude — nothing
 * gated it to an actual impact. Sourced from ball speed already in
 * CameraInput (no new plumbing): only a strike near shot pace ARMS the shake,
 * and it decays to nothing well under a second, so a sustained hard clearance
 * does not shake the whole time it is in flight.
 */
const SHAKE_TRIGGER_BALL_SPEED = 22; // m/s — a genuinely hard hit, not a jog-pace pass
const SHAKE_DECAY_S = 0.35;

// ── Stand/touchline occlusion clamp (§7.2 "Bounds") ─────────────────────────
/**
 * Mirrors render/stadium.ts's SIDE_MARGIN(7) / END_MARGIN(10) / TIER1.depth0
 * (1.5) — the ring line sits `*_MARGIN` outside the pitch, and the solid front
 * wall of the bowl starts `STAND_WALL_STANDOFF` further out still. Duplicated
 * here rather than imported: camera.ts is deliberately three.js-free (file
 * header), and stadium.ts pulls in a full three.js scene graph. READ-ONLY —
 * nothing enforces these staying in sync with stadium.ts except this comment;
 * if those numbers ever move, move these with them by hand.
 */
const STAND_MARGIN_SIDE = 7;
const STAND_MARGIN_END = 10;
const STAND_WALL_STANDOFF = 1.5;
/** Extra clearance kept beyond the solid wall face — room for the smooth
 *  inward blend to work before the hard backstop clamp would ever engage. */
const STAND_SAFETY_BUFFER = 2.0;
/** The inward blend starts easing in this many metres before the safe line. */
const STAND_BLEND_BAND = 10;
/** Floor for the broadcast pull-back distance when hugging a line/corner. */
const STAND_MIN_DISTANCE_SCALE = 0.45;
/** How much height compensates for the lost distance — "blend the camera
 *  inward/up" so the player (and the space around them) stays framed from a
 *  steeper angle rather than just getting closer. */
const STAND_HEIGHT_BOOST_SCALE = 0.35;

function dampState(): SmoothDampState {
  return { velocity: 0 };
}

/**
 * smoothDamp with an EXPLICIT ceiling on the implied acceleration this tick —
 * a designer-facing cap independent of smoothTime tuning. smoothTime alone
 * bounds how fast the spring reacts on average; this bounds the worst single
 * tick, which is what actually reads as a snap (§7.2's rotation/zoom caps).
 */
function smoothDampCapped(
  current: number,
  target: number,
  state: SmoothDampState,
  smoothTime: number,
  dt: number,
  maxAccel: number,
): number {
  const prevVel = state.velocity;
  const out = smoothDamp(current, target, state, smoothTime, dt);
  const accel = (state.velocity - prevVel) / dt;
  if (accel > maxAccel) {
    state.velocity = prevVel + maxAccel * dt;
  } else if (accel < -maxAccel) {
    state.velocity = prevVel - maxAccel * dt;
  } else {
    return out;
  }
  return current + state.velocity * dt;
}

/**
 * Same, for an angle in radians — unwraps `target` to the shortest path from
 * `current` first so the spring never takes the long way round (§4.1's yaw
 * convention: atan2(z, x)). `current` is left free to grow unbounded across
 * calls (never wrapped) — only its cos/sin are ever read, and those are
 * periodic anyway, so an unwrapped running angle is simpler and exact.
 */
function smoothDampAngleCapped(
  current: number,
  target: number,
  state: SmoothDampState,
  smoothTime: number,
  dt: number,
  maxAccel: number,
): number {
  let delta = target - current;
  delta -= Math.round(delta / (2 * Math.PI)) * 2 * Math.PI;
  return smoothDampCapped(current, current + delta, state, smoothTime, dt, maxAccel);
}

// ── Stand-boundary geometry (pure — no three.js) ────────────────────────────

/** One half-plane of the safe-boundary octagon: inside ⇔ x·nx + z·nz ≤ offset. */
export interface StandEdge {
  readonly nx: number;
  readonly nz: number;
  readonly offset: number;
}

/**
 * The same 8-point bowl outline as render/stadium.ts's `bowlRingPoints()`
 * (4 straight stands + 4 chamfered corners), inset by the wall standoff minus
 * the safety buffer so `offset` is the actual SAFE limit, not the notional
 * ring line. Exported so camera.test.ts can assert the "never crosses the
 * stand" invariant against the EXACT geometry the runtime clamp uses — one
 * source of truth, not two formulas that can silently drift apart.
 */
export function standEdges(halfL: number, halfW: number): StandEdge[] {
  const standoff = STAND_WALL_STANDOFF - STAND_SAFETY_BUFFER;
  const sZ = halfW + STAND_MARGIN_SIDE;
  const sX = halfL + STAND_MARGIN_END;
  const px = [-halfL, halfL, sX, sX, halfL, -halfL, -sX, -sX];
  const pz = [-sZ, -sZ, -halfW, halfW, sZ, sZ, halfW, -halfW];
  const edges: StandEdge[] = [];
  for (let i = 0; i < 8; i++) {
    const j = (i + 1) % 8;
    const dx = px[j]! - px[i]!;
    const dz = pz[j]! - pz[i]!;
    const len = Math.hypot(dx, dz);
    const nx = dz / len;
    const nz = -dx / len;
    edges.push({ nx, nz, offset: nx * px[i]! + nz * pz[i]! + standoff });
  }
  return edges;
}

/** True once (x,z) has crossed the safe stand boundary on ANY edge. */
export function isOutsideStandBounds(x: number, z: number, edges: readonly StandEdge[]): boolean {
  for (const e of edges) {
    if (x * e.nx + z * e.nz - e.offset > 1e-6) return true;
  }
  return false;
}

/** How close (0..1) a point is to the safe boundary, ramping up over the last
 *  STAND_BLEND_BAND metres before it — the smooth "blend inward/up" driver. */
function standProximityBlend(x: number, z: number, edges: readonly StandEdge[]): number {
  let maxVal = -Infinity;
  for (const e of edges) {
    const leadOffset = e.offset - STAND_BLEND_BAND;
    const v = x * e.nx + z * e.nz - leadOffset;
    if (v > maxVal) maxVal = v;
  }
  return clamp01(maxVal / STAND_BLEND_BAND);
}

/** Exact backstop: project (x,z) back inside the safe polygon if it has
 *  crossed any edge. Two sweeps is enough for an 8-edge convex shape with
 *  ≤45° corners — this is what the acceptance test actually depends on; the
 *  smooth blend above is what makes it look good rather than abrupt. */
function clampToStandBounds(
  x: number,
  z: number,
  edges: readonly StandEdge[],
  out: { x: number; z: number },
): void {
  let px = x;
  let pz = z;
  for (let pass = 0; pass < 2; pass++) {
    for (const e of edges) {
      const d = px * e.nx + pz * e.nz - e.offset;
      if (d > 0) {
        px -= e.nx * d;
        pz -= e.nz * d;
      }
    }
  }
  out.x = px;
  out.z = pz;
}

export class CameraSystem {
  mode: CameraMode = CameraMode.Broadcast;

  /** The current pose. Overwritten in place every frame — never allocated. */
  readonly pose: CameraPose = {
    x: 0, y: CAMERA_BROADCAST_HEIGHT, z: CAMERA_BROADCAST_DISTANCE,
    lookX: 0, lookY: 0, lookZ: 0,
    fov: CAMERA_BROADCAST_FOV,
    showBallArrow: false,
    ballArrowYaw: 0,
    offBallBlend: 0,
  };

  /**
   * Dead-zone anchor (§7.2). The camera chases THIS, not the raw target: while
   * the raw target stays within CAMERA_DEAD_ZONE of the anchor the anchor does
   * not move at all, so a ball jittering at someone's feet does not shake the
   * whole frame. Without it the smoothing alone would still transmit every
   * touch as a small camera twitch.
   */
  private anchorX = 0;
  private anchorZ = 0;
  private anchorInit = false;

  // ── Stage 2: camera-position spring (shared by field cams + shoulder cams,
  // one mode is ever active at a time so sharing state is safe). ────────────
  private readonly sx = dampState();
  private readonly sz = dampState();
  private readonly sy = dampState();
  private readonly sFov = dampState();

  private smoothX = 0;
  private smoothY = 0;
  private smoothZ = 0;
  private smoothFov = CAMERA_BROADCAST_FOV;

  // ── Stage 1 (field cam): pre-filters the dead-zone anchor before the
  // camera-position spring ever sees it (§7.2 dizziness fix). ───────────────
  private readonly tgtSX = dampState();
  private readonly tgtSZ = dampState();
  private targetX = 0;
  private targetZ = 0;

  // ── Ball low-pass (§7.2 "low-pass the ball-follow contribution"). ─────────
  private readonly ballSX = dampState();
  private readonly ballSZ = dampState();
  private readonly ballSY = dampState();
  private ballSmoothX = 0;
  private ballSmoothZ = 0;
  private ballSmoothY = 0;
  private ballFilterInit = false;

  // ── Stage 1 (shoulder cam): pre-filters the raw player position + yaw. ────
  private readonly tpx = dampState();
  private readonly tpy = dampState();
  private readonly tpz = dampState();
  private readonly syaw = dampState();
  private targetPX = 0;
  private targetPY = 0;
  private targetPZ = 0;
  private smoothYaw = 0;
  private shoulderInit = false;

  // ── §10 tactical orbit: its OWN mouse-driven yaw spring, isolated from the
  // shoulder cam's so switching between them never carries a stale angle. The
  // yaw is fed ONLY from the mouse-look yaw (main.ts's cameraLookYaw) — never
  // from movement, the pass or the ball — which IS the "no yaw coupling" rule. ─
  private readonly orbitYawState = dampState();
  private orbitYaw = 0;
  private orbitInit = false;

  // ── Shake: an impulse envelope armed by a hard hit, not a constant loop. ──
  private shakeEnvelope = 0;
  private prevBallSpeed = 0;
  /** standEdges memo — see update(); keyed on the pitch dimensions. */
  private _edges: StandEdge[] | null = null;
  private _edgesHalfL = 0;
  private _edgesHalfW = 0;

  // ── Zero-alloc scratch for the stand-boundary backstop clamp. ─────────────
  private readonly standScratch = { x: 0, z: 0 };

  /** Free-cam pose, set externally (spectator/replay). */
  private freeX = 0;
  private freeY = 30;
  private freeZ = 40;

  setMode(m: CameraMode): void {
    this.mode = m;
  }

  cycleMode(): CameraMode {
    const i = CAMERA_CYCLE.indexOf(this.mode);
    // An unlisted mode (Free) cycles back to the head of the list.
    this.mode = CAMERA_CYCLE[(i + 1) % CAMERA_CYCLE.length] ?? CameraMode.Broadcast;
    return this.mode;
  }

  setFreePose(x: number, y: number, z: number): void {
    this.freeX = x;
    this.freeY = y;
    this.freeZ = z;
  }

  /** Snap the smoothing to the current target — call on kickoff/teleport. */
  reset(): void {
    this.anchorInit = false;
    this.sx.velocity = 0;
    this.sz.velocity = 0;
    this.sy.velocity = 0;
    this.sFov.velocity = 0;
    this.tgtSX.velocity = 0;
    this.tgtSZ.velocity = 0;
    this.ballSX.velocity = 0;
    this.ballSZ.velocity = 0;
    this.ballSY.velocity = 0;
    this.ballFilterInit = false;
    this.tpx.velocity = 0;
    this.tpy.velocity = 0;
    this.tpz.velocity = 0;
    this.syaw.velocity = 0;
    this.shoulderInit = false;
    this.orbitYawState.velocity = 0;
    this.orbitInit = false;
    this.shakeEnvelope = 0;
    this.prevBallSpeed = 0;
  }

  update(input: CameraInput, dtS: number): CameraPose {
    const t = TUNABLES.camera;
    const dt = dtS > 0.1 ? 0.1 : dtS; // a stalled frame must not fling the camera

    // Off-ball blend (§7.3) — computed for every mode because the HUD's ball
    // arrow depends on it even when the camera itself ignores the ball. Uses
    // the RAW ball/player distance deliberately: this is a mode-selection
    // policy value, not part of the smoothed follow path, and camera.test.ts
    // pins it reacting within a single frame.
    const toBallX = input.ballX - input.playerX;
    const toBallZ = input.ballZ - input.playerZ;
    const ballDist = Math.hypot(toBallX, toBallZ);
    const blend = clamp01(
      (ballDist - CAMERA_OFFBALL_BLEND_START) /
        (CAMERA_OFFBALL_BLEND_FULL - CAMERA_OFFBALL_BLEND_START),
    );
    this.pose.offBallBlend = blend;
    this.pose.showBallArrow = ballDist > CAMERA_OFFBALL_BLEND_FULL;
    // Sim yaw convention (player-controller.ts): yaw = atan2(z, x), so yaw 0
    // faces +X. Everything downstream assumes it — do not "fix" the argument
    // order to the more familiar atan2(x, z).
    this.pose.ballArrowYaw = Math.atan2(toBallZ, toBallX);

    // Ball low-pass (§7.2) — advanced every frame regardless of mode so it is
    // warm the instant a mode switch brings a field cam back into play.
    if (!this.ballFilterInit) {
      this.ballSmoothX = input.ballX;
      this.ballSmoothZ = input.ballZ;
      this.ballSmoothY = input.ballY;
      this.ballFilterInit = true;
    } else {
      this.ballSmoothX = smoothDamp(this.ballSmoothX, input.ballX, this.ballSX, BALL_FOLLOW_SMOOTH_TIME, dt);
      this.ballSmoothZ = smoothDamp(this.ballSmoothZ, input.ballZ, this.ballSZ, BALL_FOLLOW_SMOOTH_TIME, dt);
      this.ballSmoothY = smoothDamp(this.ballSmoothY, input.ballY, this.ballSY, BALL_FOLLOW_SMOOTH_TIME, dt);
    }

    // Shake envelope (§7.2) — a rising edge on ball speed arms it, then it
    // decays every frame regardless of mode. Only DYNAMIC ever reads it.
    const ballSpeed = Math.hypot(input.ballVX, input.ballVZ);
    if (ballSpeed > SHAKE_TRIGGER_BALL_SPEED && this.prevBallSpeed <= SHAKE_TRIGGER_BALL_SPEED) {
      this.shakeEnvelope = 1;
    }
    this.prevBallSpeed = ballSpeed;
    this.shakeEnvelope *= Math.exp(-dt / SHAKE_DECAY_S);

    if (this.mode === CameraMode.Free) {
      this.pose.x = this.freeX;
      this.pose.y = this.freeY;
      this.pose.z = this.freeZ;
      this.pose.lookX = input.ballX;
      this.pose.lookY = input.ballY;
      this.pose.lookZ = input.ballZ;
      this.pose.fov = t.fov;
      return this.pose;
    }

    // Memoized: standEdges allocates 8 edge objects and depends only on the
    // pitch dimensions, which change never (or at most on a mode switch) —
    // rebuilding it per frame was the loop's one stray allocation.
    if (
      this._edges === null ||
      this._edgesHalfL !== input.pitchHalfLength ||
      this._edgesHalfW !== input.pitchHalfWidth
    ) {
      this._edges = standEdges(input.pitchHalfLength, input.pitchHalfWidth);
      this._edgesHalfL = input.pitchHalfLength;
      this._edgesHalfW = input.pitchHalfWidth;
    }
    const edges = this._edges;

    if (this.mode === CameraMode.PlayerCam || this.mode === CameraMode.ProCam) {
      this.updateShoulderCam(input, dt, edges);
      return this.pose;
    }

    if (this.mode === CameraMode.TacticalFollow) {
      return this.updateTacticalFollow(input, dt, blend, edges);
    }

    return this.updateFieldCam(input, dt, blend, edges);
  }

  /**
   * Steps 1–6 of the field-cam follow: anticipation → weighted centroid →
   * off-ball blend → dead zone → framing bounds → stage-1 smoothing. Leaves the
   * SMOOTHED follow point in this.targetX / this.targetZ. Factored out because
   * the §10 tactical cam tracks the exact same point the broadcast cam does — it
   * only differs in where it puts the camera RELATIVE to that point. Extracted
   * verbatim, so the broadcast/tele/dynamic path is unchanged bit-for-bit.
   */
  private followTarget(input: CameraInput, dt: number, blend: number): void {
    const t = TUNABLES.camera;

    // ── 1. Anticipation (§7.2) ─────────────────────────────────────────────
    const antX = this.ballSmoothX + input.ballVX * t.anticipation;
    const antZ = this.ballSmoothZ + input.ballVZ * t.anticipation;

    // ── 2. Weighted centroid (§7.1) ────────────────────────────────────────
    let targetX = antX * CAMERA_BALL_WEIGHT + input.playerX * CAMERA_PLAYER_WEIGHT;
    let targetZ = antZ * CAMERA_BALL_WEIGHT + input.playerZ * CAMERA_PLAYER_WEIGHT;

    // ── 3. Off-ball blend (§7.3) ───────────────────────────────────────────
    if (blend > 0) {
      targetX += (input.playerX - targetX) * blend;
      targetZ += (input.playerZ - targetZ) * blend;
    }

    // ── 4. Dead zone (§7.2) ────────────────────────────────────────────────
    if (!this.anchorInit) {
      this.anchorX = targetX;
      this.anchorZ = targetZ;
      this.anchorInit = true;
    } else {
      const dx = targetX - this.anchorX;
      const dz = targetZ - this.anchorZ;
      const d = Math.hypot(dx, dz);
      if (d > CAMERA_DEAD_ZONE) {
        const pull = (d - CAMERA_DEAD_ZONE) / d;
        this.anchorX += dx * pull;
        this.anchorZ += dz * pull;
      }
    }

    // ── 5. Bounds (framing, §7.2) ───────────────────────────────────────────
    const limX = input.pitchHalfLength + BOUNDS_MARGIN;
    const limZ = input.pitchHalfWidth + BOUNDS_MARGIN;
    const clampedX = clamp(this.anchorX, -limX, limX);
    const clampedZ = clamp(this.anchorZ, -limZ, limZ);

    // ── 6. Stage-1 target smoothing (§7.2 dizziness fix) ───────────────────
    this.targetX = smoothDamp(this.targetX, clampedX, this.tgtSX, TARGET_SMOOTH_TIME, dt);
    this.targetZ = smoothDamp(this.targetZ, clampedZ, this.tgtSZ, TARGET_SMOOTH_TIME, dt);
  }

  /** BROADCAST / TELE / DYNAMIC — the elevated side-on family. */
  private updateFieldCam(
    input: CameraInput,
    dt: number,
    blend: number,
    edges: readonly StandEdge[],
  ): CameraPose {
    const t = TUNABLES.camera;

    // ── 1–6. Anticipation → centroid → off-ball blend → dead zone → framing
    // bounds → stage-1 smoothing. Shared with the §10 tactical cam; the SMOOTHED
    // follow point lands in this.targetX / this.targetZ. ───────────────────────
    this.followTarget(input, dt, blend);

    // ── 7. Mode shaping ────────────────────────────────────────────────────
    const tele = this.mode === CameraMode.Tele;
    let height = t.height * (tele ? TELE_HEIGHT_SCALE : 1);
    let distance = t.distance * (tele ? TELE_DISTANCE_SCALE : 1);

    let fov = t.fov + (tele ? TELE_FOV_BONUS : 0);
    // FOV kick (§7.2) — "+5° when sprinting. Sells speed for free."
    if (input.sprinting) fov += CAMERA_SPRINT_FOV_KICK;
    if (this.mode === CameraMode.Dynamic) fov += input.playerSpeed * DYNAMIC_FOV_PER_SPEED;
    fov += OFFBALL_FOV_WIDEN * blend;

    // ── 8. Stand-avoidance blend (§7.2, user report: blocked by the audience) ─
    // The rig always sits `distance` further out along +Z than the (already
    // smoothed) follow target — check THAT prospective camera position against
    // the stand boundary, not the target itself, since the target alone can be
    // safely inside while the pulled-back camera is not. Blends distance down
    // and height up smoothly (input is already low-pass filtered, so this stays
    // smooth too) rather than waiting to hit a hard limit.
    const standBlend = standProximityBlend(this.targetX, this.targetZ + distance, edges);
    if (standBlend > 0) {
      distance *= 1 - (1 - STAND_MIN_DISTANCE_SCALE) * standBlend;
      height *= 1 + STAND_HEIGHT_BOOST_SCALE * standBlend;
    }

    // ── 9. Smoothing (stage 2, §7.2) ───────────────────────────────────────
    this.smoothX = smoothDamp(this.smoothX, this.targetX, this.sx, t.smoothTime, dt);
    this.smoothZ = smoothDamp(this.smoothZ, this.targetZ, this.sz, t.smoothTime, dt);
    this.smoothY = smoothDamp(this.smoothY, height, this.sy, CAMERA_SMOOTH_TIME, dt);
    this.smoothFov = smoothDampCapped(this.smoothFov, fov, this.sFov, FOV_SMOOTH_TIME, dt, ZOOM_ACCEL_CAP_DEG_S2);

    // ── 10. Pose ────────────────────────────────────────────────────────────
    // The broadcast rig sits on the +Z touchline, `distance` back and `height`
    // up, looking across the pitch.
    let camX = this.smoothX;
    let camY = this.smoothY;
    let camZ = this.smoothZ + distance;

    // Shake: an impulse on a hard hit only (§7.2) — OFF during normal running.
    if (this.mode === CameraMode.Dynamic && this.shakeEnvelope > 1e-3) {
      const s = input.elapsedS;
      const env = this.shakeEnvelope;
      camX += Math.sin(s * DYNAMIC_SHAKE_HZ) * DYNAMIC_SHAKE_AMPLITUDE * env;
      camY += Math.sin(s * DYNAMIC_SHAKE_HZ * 1.618) * DYNAMIC_SHAKE_AMPLITUDE * 0.6 * env;
    }

    // ── 11. Stand-boundary hard backstop (§7.2) ─────────────────────────────
    // The smooth blend above handles the common case; this is the exact
    // guarantee the acceptance test depends on regardless of blend tuning.
    clampToStandBounds(camX, camZ, edges, this.standScratch);
    camX = this.standScratch.x;
    camZ = this.standScratch.z;

    // Tilt (§7.1 "~15° tilt"): the rig's natural aim at the target is
    // atan(height/distance) ≈ 36° down, which frames the target dead centre and
    // shows none of the pitch beyond it. Raising the aim by the tilt angle is
    // what the 15° buys — the ball sits low-centre and you can see the space the
    // play is running into, which is what a real broadcast cut looks like.
    // Uses the LOW-PASSED ball height so a bounce doesn't visibly flick the
    // horizon tilt, and the BLENDED distance so the angle stays geometrically
    // consistent with a pulled-in, raised-up camera near the stands.
    const naturalPitch = Math.atan2(camY - this.ballSmoothY, distance);
    const aimPitch = Math.max(naturalPitch - CAMERA_BROADCAST_TILT_DEG * DEG2RAD, 5 * DEG2RAD);
    const aimDist = 100; // look-at is a direction; any far point on the ray works
    this.pose.x = camX;
    this.pose.y = camY;
    this.pose.z = camZ;
    this.pose.lookX = camX;
    this.pose.lookY = camY - Math.sin(aimPitch) * aimDist;
    this.pose.lookZ = camZ - Math.cos(aimPitch) * aimDist;
    this.pose.fov = this.smoothFov;
    return this.pose;
  }

  /**
   * §10 IMMERSIVE TACTICAL — follows the play like the broadcast cam but orbits
   * around it on the mouse-look yaw. The azimuth of the camera about the follow
   * point is a function of `input.playerYaw` (main.ts's mouse-only cameraLookYaw)
   * and NOTHING else: WASD, the pass and the ball move the follow POINT (which is
   * correct — the cam should track the play) but never the ANGLE you view it
   * from. That is the §10 "no yaw coupling to movement/pass/ball" rule made
   * structural — there is simply no other term in the azimuth. And because the
   * only input is the mouse, the mouse always wins: there is no auto-recentre to
   * fight it (§10 "manual mouse wins").
   *
   * ⚠ THIS IS THE DEFAULT CAMERA as of 2026-07-25 (ui/settings.ts
   * DEFAULT_CAMERA_MODE, which carries the full reasoning): the screen-centre
   * crosshair became the strike aim, and a fixed-azimuth rig cannot carry a
   * centre reticle that aims. So the orbit yaw below is not just a framing
   * preference any more — IT IS WHERE THE BALL GOES, and `crosshairAim.ts`
   * reads it back out of the pose this method writes.
   *
   * It follows that the charge no longer freezes it. §10's trajectory freeze
   * (the loops held cameraLookYaw constant while a shot/pass charged, so the
   * orbit froze with it) is gone from InputManager.feedMouseMove — freezing the
   * view now freezes the aim, which costs a mid-charge correction. The charge
   * turns at the finer `aimSensitivity` gain instead.
   */
  private updateTacticalFollow(
    input: CameraInput,
    dt: number,
    blend: number,
    edges: readonly StandEdge[],
  ): CameraPose {
    const t = TUNABLES.camera;

    // Same smoothed follow point the broadcast cam tracks (steps 1–6).
    this.followTarget(input, dt, blend);

    // Orbit yaw: mouse-only, seeded on the first tactical frame so it never
    // swings in from a stale 0, then acceleration-capped so a fast mouse flick
    // eases rather than snaps (the same "never snap" contract the shoulder cam
    // has). This is the sole source of the camera's azimuth.
    if (!this.orbitInit) {
      this.orbitYaw = input.playerYaw;
      this.orbitInit = true;
    } else {
      this.orbitYaw = smoothDampAngleCapped(
        this.orbitYaw, input.playerYaw, this.orbitYawState, TARGET_SMOOTH_TIME, dt, ROTATION_ACCEL_CAP_RAD_S2,
      );
    }
    // Sim yaw convention (player-controller.ts): forward = (cos, sin), yaw 0
    // faces +X. The camera sits BEHIND the follow point along that heading and
    // looks back down it, so pushing the mouse right sweeps the view right.
    const fx = Math.cos(this.orbitYaw);
    const fz = Math.sin(this.orbitYaw);

    let height = t.height * TACTICAL_HEIGHT_SCALE;
    let distance = t.distance * TACTICAL_DISTANCE_SCALE;
    let fov = t.fov + TACTICAL_FOV_BONUS;
    if (input.sprinting) fov += CAMERA_SPRINT_FOV_KICK;
    fov += OFFBALL_FOV_WIDEN * blend;

    // Stand-avoidance blend (§7.2) — check the prospective camera position (behind
    // the follow point along the orbit heading) against the boundary, pull in/up.
    const prospX = this.targetX - fx * distance;
    const prospZ = this.targetZ - fz * distance;
    const standBlend = standProximityBlend(prospX, prospZ, edges);
    if (standBlend > 0) {
      distance *= 1 - (1 - STAND_MIN_DISTANCE_SCALE) * standBlend;
      height *= 1 + STAND_HEIGHT_BOOST_SCALE * standBlend;
    }

    const targetCamX = this.targetX - fx * distance;
    const targetCamZ = this.targetZ - fz * distance;

    // Stage-2 position spring (shared with the other cams; one mode at a time).
    this.smoothX = smoothDamp(this.smoothX, targetCamX, this.sx, t.smoothTime, dt);
    this.smoothZ = smoothDamp(this.smoothZ, targetCamZ, this.sz, t.smoothTime, dt);
    this.smoothY = smoothDamp(this.smoothY, height, this.sy, CAMERA_SMOOTH_TIME, dt);
    this.smoothFov = smoothDampCapped(this.smoothFov, fov, this.sFov, FOV_SMOOTH_TIME, dt, ZOOM_ACCEL_CAP_DEG_S2);

    // Hard backstop — the same exact solid-geometry guarantee the other cams get.
    clampToStandBounds(this.smoothX, this.smoothZ, edges, this.standScratch);
    this.pose.x = this.standScratch.x;
    this.pose.y = this.smoothY;
    this.pose.z = this.standScratch.z;
    // Look at the follow point at roughly chest height — a fixed aim point so the
    // horizon does not tilt as the ball bounces. Ball Focus (opt-in) eases the aim
    // toward the CURRENT (low-passed) ball so it stays framed; the orbit yaw above
    // is untouched, so the mouse still fully owns the view direction.
    const bf = BALL_FOCUS_WEIGHT[input.ballFocus ?? 0] ?? 0;
    this.pose.lookX = this.targetX + (this.ballSmoothX - this.targetX) * bf;
    this.pose.lookY = TACTICAL_LOOK_HEIGHT;
    this.pose.lookZ = this.targetZ + (this.ballSmoothZ - this.targetZ) * bf;
    this.pose.fov = this.smoothFov;
    return this.pose;
  }

  /** PLAYER_CAM / PRO_CAM — behind the controlled player, facing their aim. */
  private updateShoulderCam(input: CameraInput, dt: number, edges: readonly StandEdge[]): CameraPose {
    const pro = this.mode === CameraMode.ProCam;
    let back = pro ? PRO_CAM_BACK : PLAYER_CAM_BACK;
    let up = pro ? PRO_CAM_HEIGHT : PLAYER_CAM_HEIGHT;
    const ahead = pro ? PRO_CAM_LOOK_AHEAD : PLAYER_CAM_LOOK_AHEAD;
    let fov = pro ? PRO_CAM_FOV : PLAYER_CAM_FOV;
    if (input.sprinting) fov += CAMERA_SPRINT_FOV_KICK;

    // ── Stage 1 (§7.2 dizziness fix) — this is the actual bug fix: the look-at
    // used to be built from `input.playerX` RAW, completely unsmoothed, every
    // frame, while only the camera POSITION was springy. A runner's ±0.3m
    // per-step sway therefore reached the screen as a raw, undamped whip-pan
    // (measured pre-fix: ~45-63 rad/s² look-direction acceleration). Both the
    // camera position AND the look-at now derive from the SAME smoothed target
    // — one filtered number, not one filtered and one raw.
    if (!this.shoulderInit) {
      this.targetPX = input.playerX;
      this.targetPY = input.playerY;
      this.targetPZ = input.playerZ;
      this.smoothYaw = input.playerYaw;
      this.shoulderInit = true;
    } else {
      this.targetPX = smoothDamp(this.targetPX, input.playerX, this.tpx, TARGET_SMOOTH_TIME, dt);
      this.targetPY = smoothDamp(this.targetPY, input.playerY, this.tpy, TARGET_SMOOTH_TIME, dt);
      this.targetPZ = smoothDamp(this.targetPZ, input.playerZ, this.tpz, TARGET_SMOOTH_TIME, dt);
      this.smoothYaw = smoothDampAngleCapped(
        this.smoothYaw, input.playerYaw, this.syaw, TARGET_SMOOTH_TIME, dt, ROTATION_ACCEL_CAP_RAD_S2,
      );
    }

    // Sim yaw convention: player-controller.ts sets facing with
    // `datan2(dz, dx)` and reads it back as `vx = dcos(yaw); vz = dsin(yaw)`.
    // So forward is (cos, sin) and yaw 0 faces +X — NOT the (sin, cos)/+Z form
    // most engines use. Swapping these silently puts the camera at the player's
    // shoulder instead of behind them, which looks almost right.
    const fx = Math.cos(this.smoothYaw);
    const fz = Math.sin(this.smoothYaw);

    // Stand-avoidance blend (§7.2) — same idea as the field cam: check the
    // prospective camera position (behind the SMOOTHED player, along their
    // SMOOTHED facing) against the boundary, blend `back`/`up` accordingly.
    const prospX = this.targetPX - fx * back;
    const prospZ = this.targetPZ - fz * back;
    const standBlend = standProximityBlend(prospX, prospZ, edges);
    if (standBlend > 0) {
      back *= 1 - (1 - STAND_MIN_DISTANCE_SCALE) * standBlend;
      up *= 1 + STAND_HEIGHT_BOOST_SCALE * standBlend;
    }

    const targetX = this.targetPX - fx * back;
    const targetZ = this.targetPZ - fz * back;
    const targetY = this.targetPY + up;

    // ── Stage 2 — a shorter smooth time than the field cams: at this distance
    // the same lag reads as the camera being dragged rather than following.
    const st = TUNABLES.camera.smoothTime * 0.6;
    this.smoothX = smoothDamp(this.smoothX, targetX, this.sx, st, dt);
    this.smoothZ = smoothDamp(this.smoothZ, targetZ, this.sz, st, dt);
    this.smoothY = smoothDamp(this.smoothY, targetY, this.sy, st, dt);
    this.smoothFov = smoothDampCapped(this.smoothFov, fov, this.sFov, FOV_SMOOTH_TIME, dt, ZOOM_ACCEL_CAP_DEG_S2);

    // Hard backstop (§7.2) — the same exact guarantee the field cam gets. Only
    // x/z are ever pulled back: the stand's solid geometry occupies this (x,z)
    // footprint at every height the camera could plausibly sit at, so keeping
    // the footprint clear is sufficient regardless of y. Note this clamps the
    // OUTPUT pose only (not the spring's own persistent state) — consistent
    // with how the field cam's framing clamp already works, and it means a
    // player jammed against the line facing away from the pitch (a rare, at
    // most seconds-long state) gets corrected every frame rather than the
    // spring itself being forced off its natural path.
    clampToStandBounds(this.smoothX, this.smoothZ, edges, this.standScratch);
    this.pose.x = this.standScratch.x;
    this.pose.y = this.smoothY;
    this.pose.z = this.standScratch.z;
    this.pose.lookX = this.targetPX + fx * ahead;
    this.pose.lookY = this.targetPY + 1.0; // roughly chest height
    this.pose.lookZ = this.targetPZ + fz * ahead;
    this.pose.fov = this.smoothFov;
    // These cams are already locked to you; the arrow is what §7.3 gives a
    // field cam that has drifted off the ball, so it is redundant here.
    this.pose.showBallArrow = false;
    return this.pose;
  }

  /**
   * The ground-plane basis the InputManager needs to make WASD camera-relative
   * (§4.1 "Move (relative to camera)"). Writes into `out` — no allocation.
   */
  movementBasis(out: { forwardX: number; forwardZ: number; rightX: number; rightZ: number }): void {
    const dx = this.pose.lookX - this.pose.x;
    const dz = this.pose.lookZ - this.pose.z;
    const len = Math.hypot(dx, dz);
    if (len < 1e-6) {
      // Camera looking straight down: no meaningful ground forward. Hold the
      // broadcast default rather than dividing by ~zero and flinging the player.
      out.forwardX = 0; out.forwardZ = -1; out.rightX = 1; out.rightZ = 0;
      return;
    }
    out.forwardX = dx / len;
    out.forwardZ = dz / len;
    // Right = forward rotated -90° about Y.
    out.rightX = -out.forwardZ;
    out.rightZ = out.forwardX;
  }
}

/** Anticipation constant re-exported so the dev overlay can label it. (§7.2) */
export { CAMERA_ANTICIPATION_S };
